import { Tooltip } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ICONS_TYPES } from '../../../Icon';

import { NavItemId, NavItemType } from '../../enums';

import {
  ArrowIcon,
  IconAndTitleContainer,
  IconWrapper,
  NavLinkContainer,
  NavLinkIcon,
  NavLinkTitle,
  Root,
  SectionDivider,
  SectionTitle,
  StyledNavLink,
  SubMenuContainer,
  SubMenuNavLinkContainer,
  SubMenuStyledNavLink,
} from './styles';
import { Props } from './types';

const SubMenu = ({
  className,
  item,
  isOpenedNavBar,
  showSubNav,
  setShowSubNav,
  onCollapsedClick,
}: Props) => {
  const location = useLocation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);

  useEffect(() => {
    if (isOpenedNavBar) setTooltipOpen(false);
  }, [isOpenedNavBar]);

  const handleClickSubNav = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();

      setShowSubNav((prev) =>
        prev.includes(item.title)
          ? prev.filter((list) => list != item.title)
          : [...prev, item.title],
      );
    },
    [item.title, setShowSubNav],
  );

  const handleClick = () => {
    setTooltipOpen(false);
    if (!isOpenedNavBar && onCollapsedClick) {
      setIsExpanding(true); // block tooltip during expansion
      onCollapsedClick();
    }
  };

  useEffect(() => {
    if (isOpenedNavBar) {
      setTooltipOpen(false);
      setIsExpanding(false); // reset once sidebar is fully open
    }
  }, [isOpenedNavBar]);

  return (
    <Root className={className}>
      {item?.type === NavItemType.SECTION && (
        <>
          <SectionDivider />
          {isOpenedNavBar && <SectionTitle>{item.title}</SectionTitle>}
        </>
      )}
      {item?.icon && (
        <NavLinkContainer>
          <StyledNavLink
            to={item.path || '#'}
            $showDot={item.title === NavItemId.DASHBOARD}
            $isActive={
              location.pathname === item.path ||
              !!item.subNav?.some((sub) => location.pathname === sub.path)
            }
            $isOpenedNavBar={isOpenedNavBar}>
            <IconAndTitleContainer>
              {item?.icon && (
                <Tooltip
                  title={item.title}
                  open={!isOpenedNavBar && tooltipOpen}
                  onOpen={() =>
                    !isOpenedNavBar && !isExpanding && setTooltipOpen(true)
                  }
                  onClose={() => setTooltipOpen(false)}
                  disableHoverListener={isOpenedNavBar || isExpanding}
                  placement="right">
                  <IconWrapper onClick={handleClick}>
                    <NavLinkIcon icon={`${item?.icon}`} size={20} />
                  </IconWrapper>
                </Tooltip>
              )}
              {isOpenedNavBar && <NavLinkTitle>{item.title}</NavLinkTitle>}
            </IconAndTitleContainer>

            {item.subNav && isOpenedNavBar && (
              <ArrowIcon
                icon={ICONS_TYPES['ChevronDown']}
                rotate={showSubNav.includes(item.title) ? 0 : -90}
                size={18}
                onClick={item.subNav ? handleClickSubNav : undefined}
              />
            )}
          </StyledNavLink>
        </NavLinkContainer>
      )}
      {showSubNav.includes(item.title) && item?.subNav && (
        <SubMenuContainer>
          {item.subNav.map((subItem, index) => (
            <SubMenuNavLinkContainer key={index}>
              <SubMenuStyledNavLink
                to={subItem.path}
                $subLevel={1}
                $isActive={location.pathname === subItem.path}>
                <IconAndTitleContainer>
                  {isOpenedNavBar && (
                    <NavLinkTitle>{subItem.title}</NavLinkTitle>
                  )}
                </IconAndTitleContainer>
              </SubMenuStyledNavLink>
            </SubMenuNavLinkContainer>
          ))}
        </SubMenuContainer>
      )}
    </Root>
  );
};

export default SubMenu;
